import { useEffect, useState } from 'react';
import { MP_MONTH_OPTIONS, type MpMonths } from '@pos/core';
import { useSession } from '../../app/current.tsx';
import { callFunction, supabase } from '../../lib/supabase.ts';
import { useOnline } from '../../lib/use-online.ts';
import { refreshLicense } from '../../services/license.ts';
import { Alert, Button, Select, errorMessage } from '../../ui/index.tsx';
import { useMoney } from '../products/data.ts';

/**
 * Pagar la suscripción con Mercado Pago (dueño): elige los meses, va a Mercado Pago y vuelve.
 * Se acredita solo (aviso de Mercado Pago o confirmación al volver).
 */
export function PayWithMp({ planCode }: { planCode: string | null }) {
  const { business } = useSession();
  const online = useOnline();
  const money = useMoney();
  const [months, setMonths] = useState<MpMonths>(1);
  const [price, setPrice] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!online || !planCode) return;
    let cancelled = false;
    void supabase
      .from('plans')
      .select('price')
      .eq('code', planCode)
      .maybeSingle()
      .then(({ data }) => !cancelled && setPrice(data ? Number(data.price) : null));
    return () => {
      cancelled = true;
    };
  }, [online, planCode]);

  // Plan sin costo (por ejemplo, uno de cortesía): no hay nada que pagar.
  if (price === 0) return null;

  const pay = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await callFunction<{ url: string }>('mp-checkout', {
        action: 'create',
        business_id: business.id,
        months,
        return_url: window.location.origin,
      });
      window.location.href = res.url;
    } catch (e) {
      setError(errorMessage(e));
      setBusy(false);
    }
  };

  return (
    <div className="space-y-2 rounded-lg p-3 text-left ring-1 ring-slate-200">
      <PaymentReturn />
      <p className="font-medium">Pagar con Mercado Pago</p>
      <p className="text-xs text-slate-500">Con dinero en cuenta, tarjeta de débito o crédito, o efectivo en Rapipago o Pago Fácil. Se acredita solo.</p>
      <div className="flex flex-wrap items-center gap-2">
        <div className="w-48">
          <Select compact value={months} onChange={(e) => setMonths(Number(e.target.value) as MpMonths)} aria-label="Meses a pagar">
            {MP_MONTH_OPTIONS.map((m) => (
              <option key={m} value={m}>
                {m} {m === 1 ? 'mes' : 'meses'}
                {price ? ` · ${money(price * m)}` : ''}
              </option>
            ))}
          </Select>
        </div>
        <Button size="sm" loading={busy} disabled={!online || price === 0} onClick={pay}>
          Pagar{price ? ` ${money(price * months)}` : ''}
        </Button>
      </div>
      {!online && <p className="text-xs text-amber-700">Necesita conexión a internet.</p>}
      {error && <Alert tone="error">{error}</Alert>}
    </div>
  );
}

type Notice = { tone: 'success' | 'info' | 'warning' | 'error'; text: string };

const PENDING: Notice = { tone: 'info', text: 'El pago quedó pendiente (por ejemplo, en efectivo): se acredita solo cuando Mercado Pago lo confirme.' };
const FAILED: Notice = { tone: 'error', text: 'El pago no se completó. Podés intentar de nuevo.' };

/** Al volver de Mercado Pago (?pago=ok|pendiente|error): confirma el pago y actualiza la licencia. */
function PaymentReturn() {
  const { business, device } = useSession();
  const [notice, setNotice] = useState<Notice | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const result = params.get('pago');
    if (!result) {
      // Sin volver de Mercado Pago: igual se revisa si quedó algún pago aprobado sin acreditar.
      if (!navigator.onLine) return;
      void callFunction<{ credited: number }>('mp-checkout', { action: 'sync', business_id: business.id })
        .then(async (r) => {
          if (r.credited > 0) {
            setNotice({ tone: 'success', text: r.credited === 1 ? 'Se acreditó un pago de Mercado Pago. ¡Gracias!' : `Se acreditaron ${r.credited} pagos de Mercado Pago. ¡Gracias!` });
            await refreshLicense(device.id);
          }
        })
        .catch(() => undefined);
      return;
    }
    const paymentId = params.get('payment_id') ?? params.get('collection_id');
    // Sin los parámetros en la URL (así, recargar no repite la confirmación).
    window.history.replaceState(null, '', window.location.pathname);
    if (result === 'error') {
      setNotice(FAILED);
      return;
    }
    setNotice(result === 'pendiente' ? PENDING : { tone: 'info', text: 'Verificando el pago…' });
    void (async () => {
      if (!paymentId || !/^\d+$/.test(paymentId)) {
        // Sin número de pago en la vuelta: se buscan los pagos pendientes del negocio.
        await callFunction<{ credited: number }>('mp-checkout', { action: 'sync', business_id: business.id })
          .then((r) => r.credited > 0 && setNotice({ tone: 'success', text: '¡Pago acreditado! Gracias.' }))
          .catch(() => undefined);
      } else {
        try {
          const r = await callFunction<{ status: string }>('mp-checkout', { action: 'confirm', business_id: business.id, payment_id: paymentId });
          setNotice(r.status === 'approved' ? { tone: 'success', text: '¡Pago acreditado! Gracias.' } : r.status === 'rejected' ? FAILED : PENDING);
        } catch {
          setNotice({ tone: 'warning', text: 'Todavía no se pudo verificar el pago: se acredita solo en unos minutos.' });
        }
      }
      await refreshLicense(device.id).catch(() => undefined);
    })();
  }, [business.id, device.id]);

  return notice ? <Alert tone={notice.tone}>{notice.text}</Alert> : null;
}
